import { useEffect, useRef, useState } from "react";
import { Beaker, Send, Sparkles, Edit3, RotateCw, Check, X, HelpCircle, BookMarked, Image as ImageIcon, LinkIcon, Palette, Play, Plus, Trash2, Save, BarChart3, GraduationCap, RefreshCw, FileText, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";

const TABS = [
  { key: "chat", label: "Chat & Test", icon: Beaker },
  { key: "faqdoc", label: "FAQ Document", icon: FileText },
  { key: "unanswered", label: "Unanswered Questions", icon: HelpCircle },
  { key: "followups", label: "Follow-ups", icon: Clock },
  { key: "library", label: "Test Library", icon: BookMarked },
  { key: "images", label: "Image Library", icon: ImageIcon },
  { key: "analytics", label: "Question Analytics", icon: BarChart3 },
  { key: "links", label: "Check Links", icon: LinkIcon },
  { key: "background", label: "Background", icon: Palette },
];

export default function Studio() {
  const [tab, setTab] = useState("chat");
  const [bg, setBg] = useState({ background_url: "", background_opacity: 0.2 });

  useEffect(() => {
    api.get("/studio/settings").then(r => setBg(r.data)).catch(()=>{});
  }, []);

  return (
    <div className="relative min-h-[calc(100vh-4rem)]" style={bg.background_url ? { backgroundImage: `url(${bg.background_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
      {bg.background_url && <div className="absolute inset-0 bg-[#F5F2EB]" style={{ opacity: 1 - bg.background_opacity }}/>}
      <div className="relative">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-4xl flex items-center gap-3"><Sparkles className="w-8 h-8 text-[#B84F3E]" strokeWidth={1.5}/>AI Training Studio</h1>
            <p className="text-[#8A7970] mt-1">Test, correct and approve your bot — all from here. No developer needed.</p>
          </div>
        </div>
        <div className="mb-6 flex gap-2 flex-wrap">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`studio-tab-${t.key}`}
              className={`px-4 py-2 rounded-full text-sm border flex items-center gap-2 ${tab === t.key ? "bg-[#B84F3E] text-white border-[#B84F3E]" : "bg-white border-[#E7C2A6] hover:bg-[#F5F2EB]"}`}>
              <t.icon className="w-4 h-4" strokeWidth={1.5}/>{t.label}
            </button>
          ))}
        </div>
        {tab === "chat" && <ChatTab/>}
        {tab === "faqdoc" && <FAQDocTab/>}
        {tab === "unanswered" && <UnansweredTab/>}
        {tab === "followups" && <FollowupsTab/>}
        {tab === "library" && <LibraryTab/>}
        {tab === "images" && <ImagesTab/>}
        {tab === "analytics" && <AnalyticsTab/>}
        {tab === "links" && <LinksTab/>}
        {tab === "background" && <BgTab bg={bg} setBg={setBg}/>}
      </div>
    </div>
  );
}

// -------- ANALYTICS TAB --------
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get("/studio/analytics", { params: { days } });
      setData(r.data);
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [days]);

  const maxCount = data ? Math.max(...data.topics.map(t => t.count), 1) : 1;

  return (
    <div className="space-y-4">
      <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40 flex items-center justify-between">
        <div>
          <h3 className="font-serif text-xl">What guests ask about</h3>
          <p className="text-xs text-[#8A7970]">Real questions from WhatsApp + Studio conversations, bucketed by topic.</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={days} onChange={e => setDays(parseInt(e.target.value))} className="h-9 rounded-md border border-[#E7C2A6] bg-white px-3 text-sm">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last year</option>
          </select>
          <Button onClick={load} disabled={busy} variant="outline" size="sm">Refresh</Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="Guest Messages" value={data.total_messages}/>
            <Stat label="Unique Conversations" value={data.unique_conversations} color="#B84F3E"/>
            <Stat label="Unclassified" value={data.unclassified} color="#8A7970"/>
          </div>

          <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40" data-testid="analytics-topics">
            <h4 className="font-serif text-lg mb-4">Top Topics</h4>
            <div className="space-y-3">
              {data.topics.map(t => (
                <div key={t.topic}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium">{t.topic}</span>
                    <span className="text-[#8A7970]">{t.count} question{t.count !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="h-3 rounded-full bg-[#F5F2EB] overflow-hidden">
                    <div className="h-full bg-[#B84F3E] transition-all" style={{ width: `${(t.count / maxCount) * 100}%` }}/>
                  </div>
                  {t.samples.length > 0 && (
                    <div className="text-xs text-[#8A7970] mt-1 italic">
                      "{t.samples[0]}"{t.samples.length > 1 && ` · +${t.samples.length - 1} more`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#FBE8DA] rounded-xl p-5 border border-[#E7C2A6]/40 text-sm">
            💡 <b>Insight:</b> Top asked topics reveal what your guests care about most. Consider optimizing your landing page, Meta ads, and Teach-the-AI entries around them. Use "Test Library" to add regression questions for the top 3-5 topics.
          </div>
        </>
      )}
    </div>
  );
}

// -------- CHAT TAB (with inline edit/add/delete) --------
function ChatTab() {
  const [sid, setSid] = useState(`studio_${Date.now()}`);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [editing, setEditing] = useState(null); // { idx, mode:'edit'|'add', form:{id?,trigger,response,category,priority,active} }
  const [greetEdit, setGreetEdit] = useState(null); // form same shape, or null
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, editing, greetEdit]);

  const send = async (text) => {
    const q = text || input;
    if (!q.trim() || busy) return;
    setMsgs(m => [...m, { role: "user", message: q }]);
    setInput("");
    setBusy(true);
    setEditing(null);
    try {
      const [chatR, anR] = await Promise.all([
        api.post("/chat/message", { session_id: sid, message: q }),
        api.post("/studio/analyze", { session_id: sid, message: q }).catch(() => ({ data: null })),
      ]);
      setMsgs(m => [...m, { role: "assistant", message: chatR.data.reply, lang: chatR.data.lang, analysis: anR.data, userQuestion: q }]);
      setAnalysis(anR.data);
    } catch { toast.error("AI failed"); }
    finally { setBusy(false); }
  };

  const retest = (userMsg, cutIdx) => {
    if (!userMsg) return;
    setMsgs(m => m.slice(0, cutIdx));
    setEditing(null);
    send(userMsg);
  };

  const reset = () => { setSid(`studio_${Date.now()}`); setMsgs([]); setAnalysis(null); setEditing(null); setGreetEdit(null); };

  const copyConv = () => {
    const text = msgs.map(m => `${m.role === "user" ? "GUEST" : "AI"}: ${m.message}`).join("\n\n");
    navigator.clipboard.writeText(text); toast.success("Copied conversation");
  };

  const openEditForMsg = (i, m) => {
    const kb = m.analysis?.matched?.[0]?.item;
    if (kb) {
      setEditing({ idx: i, mode: "edit", form: { id: kb.id, trigger: kb.trigger, response: kb.response, category: kb.category || "General", priority: kb.priority || 5, active: kb.active !== false } });
    } else {
      // No match — teach a new lesson using current AI reply as starting point
      setEditing({ idx: i, mode: "add", form: { id: null, trigger: m.userQuestion || "", response: m.message || "", category: "General", priority: 5, active: true } });
    }
  };

  const openAddNew = (i, m) => {
    setEditing({ idx: i, mode: "add", form: { id: null, trigger: m.userQuestion || "", response: "", category: "General", priority: 5, active: true } });
  };

  const saveEdit = async () => {
    if (!editing) return;
    const f = editing.form;
    if (!f.trigger?.trim() || !f.response?.trim()) return toast.error("Trigger keywords and response are required");
    try {
      const payload = f.id ? f : { trigger: f.trigger, response: f.response, category: f.category, priority: f.priority, active: f.active };
      await api.post("/knowledge", payload);
      toast.success(f.id ? "Lesson updated — AI will use this from now on" : "New lesson added — AI will use this from now on");
      const idx = editing.idx;
      const userMsg = msgs[idx]?.userQuestion || msgs[idx - 1]?.message;
      setEditing(null);
      if (userMsg) retest(userMsg, idx);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const deleteLesson = async (kbId, idx, userMsg) => {
    if (!kbId) return;
    if (!window.confirm("Delete this lesson permanently? The AI will forget this answer.")) return;
    try {
      await api.delete(`/knowledge/${kbId}`);
      toast.success("Lesson deleted");
      setEditing(null);
      if (userMsg) retest(userMsg, idx);
    } catch { toast.error("Delete failed"); }
  };

  const openGreetingEditor = async () => {
    try {
      const r = await api.get("/knowledge");
      const g = (r.data || []).find(k => /(^|[,\s])(hi|hello|hey|welcome|namaste|greeting|start)([,\s]|$)/i.test(k.trigger || ""));
      if (g) setGreetEdit({ id: g.id, trigger: g.trigger, response: g.response, category: g.category || "Greeting", priority: g.priority || 10, active: g.active !== false });
      else setGreetEdit({ id: null, trigger: "hi, hello, hey, welcome, namaste, enquiry, start, kemon achho", response: "", category: "Greeting", priority: 10, active: true });
    } catch { toast.error("Could not load greeting"); }
  };

  const saveGreeting = async () => {
    if (!greetEdit?.trigger?.trim() || !greetEdit?.response?.trim()) return toast.error("Trigger + greeting text required");
    try {
      const payload = greetEdit.id ? greetEdit : { trigger: greetEdit.trigger, response: greetEdit.response, category: greetEdit.category, priority: greetEdit.priority, active: greetEdit.active };
      await api.post("/knowledge", payload);
      toast.success("Greeting saved — AI will greet guests with this exact message from now on");
      setGreetEdit(null);
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Chat area */}
      <div className="lg:col-span-2 bg-white/95 rounded-2xl border border-[#E7C2A6]/40 flex flex-col" style={{ height: "74vh" }} data-testid="studio-chat">
        <div className="p-4 border-b border-[#E7C2A6]/40 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm font-medium">Simulated Guest Conversation · <span className="text-[#8A7970] font-normal">edit/delete/add lessons live on this screen</span></div>
          <div className="flex gap-1 flex-wrap">
            <Button size="sm" variant="outline" onClick={openGreetingEditor} data-testid="studio-edit-greeting"><Edit3 className="w-3 h-3 mr-1"/>Edit Greeting</Button>
            <Button size="sm" variant="outline" onClick={reset} data-testid="studio-new"><Plus className="w-3 h-3 mr-1"/>New Chat</Button>
            <Button size="sm" variant="outline" onClick={copyConv} data-testid="studio-copy">Copy</Button>
          </div>
        </div>

        {/* Inline greeting editor (top) */}
        {greetEdit && (
          <div className="p-4 bg-[#FBE8DA] border-b border-[#E7C2A6]/40 space-y-2" data-testid="greeting-editor">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Edit AI Greeting — make it as vast as you like</div>
              <Button size="sm" variant="ghost" onClick={() => setGreetEdit(null)}><X className="w-3 h-3"/></Button>
            </div>
            <div className="text-xs text-[#8A7970]">This exact text will be sent as the AI's welcome to any guest who says hi / hello / namaste / etc.</div>
            <label className="text-xs text-[#8A7970]">Trigger keywords (comma separated)</label>
            <Input value={greetEdit.trigger} onChange={e => setGreetEdit({ ...greetEdit, trigger: e.target.value })} data-testid="greeting-trigger"/>
            <label className="text-xs text-[#8A7970]">Greeting response (final message the AI will send)</label>
            <Textarea rows={8} value={greetEdit.response} onChange={e => setGreetEdit({ ...greetEdit, response: e.target.value })} placeholder="Write your full welcome text here — as long and detailed as you want. Include property intro, USPs, quick nudge for check-in dates, etc." data-testid="greeting-response"/>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-[#8A7970]">Category</label><Input value={greetEdit.category} onChange={e => setGreetEdit({ ...greetEdit, category: e.target.value })}/></div>
              <div><label className="text-xs text-[#8A7970]">Priority (higher = preferred)</label><Input type="number" min={1} max={10} value={greetEdit.priority} onChange={e => setGreetEdit({ ...greetEdit, priority: parseInt(e.target.value) || 1 })}/></div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={saveGreeting} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="greeting-save"><Save className="w-3 h-3 mr-1"/>Save Greeting</Button>
              <Button size="sm" variant="outline" onClick={() => setGreetEdit(null)}>Cancel</Button>
              {greetEdit.id && <Button size="sm" variant="outline" className="text-[#B84F3E] border-[#B84F3E] ml-auto" onClick={async () => { if (window.confirm("Delete greeting lesson?")) { await api.delete(`/knowledge/${greetEdit.id}`); toast.success("Greeting deleted"); setGreetEdit(null); } }}><Trash2 className="w-3 h-3 mr-1"/>Delete</Button>}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {msgs.length === 0 && !greetEdit && (
            <div className="text-center text-[#8A7970] text-sm mt-10">
              Type a real guest question — English or Bengali. Every AI reply has <b>Edit / Add New / Delete</b> buttons that update the AI's brain instantly.
            </div>
          )}
          {msgs.map((m, i) => {
            const editingHere = editing?.idx === i;
            const kb = m.analysis?.matched?.[0]?.item;
            const userQ = m.userQuestion || msgs[i - 1]?.message;
            const isUser = m.role === "user";
            return (
              <div key={i} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                <div className={`${isUser ? "max-w-[80%] bg-[#B84F3E] text-white" : "w-full bg-[#F5F2EB] border border-[#E7C2A6]/40"} px-4 py-2.5 rounded-2xl whitespace-pre-wrap text-sm`}>
                  {(!editingHere || isUser) && m.message}

                  {/* Action row on AI messages */}
                  {!isUser && !editingHere && (
                    <div className="mt-2 pt-2 border-t border-[#E7C2A6]/30 flex flex-wrap gap-1 items-center">
                      <span className="text-[10px] uppercase text-[#8A7970] mr-1">{kb ? `Uses lesson: ${kb.category}` : "No lesson matched"}</span>
                      <Button size="sm" variant="ghost" onClick={() => retest(userQ, i - 1)} data-testid={`retest-${i}`}><RotateCw className="w-3 h-3 mr-1"/>Retest</Button>
                      <Button size="sm" variant="ghost" onClick={() => openEditForMsg(i, m)} data-testid={`edit-${i}`}><Edit3 className="w-3 h-3 mr-1"/>{kb ? "Edit Lesson" : "Teach"}</Button>
                      <Button size="sm" variant="ghost" onClick={() => openAddNew(i, m)} data-testid={`add-${i}`}><Plus className="w-3 h-3 mr-1"/>Add New</Button>
                      {kb && <Button size="sm" variant="ghost" className="text-[#B84F3E]" onClick={() => deleteLesson(kb.id, i - 1, userQ)} data-testid={`del-${i}`}><Trash2 className="w-3 h-3 mr-1"/>Delete</Button>}
                    </div>
                  )}

                  {/* Inline editor for this message */}
                  {!isUser && editingHere && (
                    <div className="space-y-2" data-testid={`inline-editor-${i}`}>
                      <div className="text-xs font-medium text-[#B84F3E]">
                        {editing.mode === "edit" ? "Editing lesson — this response becomes the AI's FINAL answer" : "Teaching a new lesson — this response becomes the AI's FINAL answer"}
                      </div>
                      <label className="text-xs text-[#8A7970]">Trigger keywords (comma separated)</label>
                      <Input value={editing.form.trigger} onChange={e => setEditing({ ...editing, form: { ...editing.form, trigger: e.target.value } })} data-testid={`edit-trigger-${i}`}/>
                      <label className="text-xs text-[#8A7970]">Final response (exactly what AI will send)</label>
                      <Textarea rows={7} value={editing.form.response} onChange={e => setEditing({ ...editing, form: { ...editing.form, response: e.target.value } })} data-testid={`edit-response-${i}`}/>
                      <div className="grid grid-cols-3 gap-2">
                        <div><label className="text-xs text-[#8A7970]">Category</label><Input value={editing.form.category} onChange={e => setEditing({ ...editing, form: { ...editing.form, category: e.target.value } })}/></div>
                        <div><label className="text-xs text-[#8A7970]">Priority</label><Input type="number" min={1} max={10} value={editing.form.priority} onChange={e => setEditing({ ...editing, form: { ...editing.form, priority: parseInt(e.target.value) || 1 } })}/></div>
                        <div className="flex items-end gap-2"><Switch checked={editing.form.active} onCheckedChange={v => setEditing({ ...editing, form: { ...editing.form, active: v } })}/><span className="text-xs">Active</span></div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button size="sm" onClick={saveEdit} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid={`save-${i}`}><Save className="w-3 h-3 mr-1"/>Save & Retest</Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(null)} data-testid={`cancel-${i}`}><X className="w-3 h-3 mr-1"/>Cancel</Button>
                        {editing.mode === "edit" && editing.form.id && (
                          <Button size="sm" variant="outline" className="text-[#B84F3E] border-[#B84F3E] ml-auto" onClick={() => deleteLesson(editing.form.id, i - 1, userQ)} data-testid={`delete-inline-${i}`}><Trash2 className="w-3 h-3 mr-1"/>Delete Lesson</Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {busy && <div className="text-center text-[#8A7970]">...</div>}
          <div ref={endRef}/>
        </div>

        <div className="p-4 border-t border-[#E7C2A6]/40 flex gap-2">
          <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()} placeholder="Type as a real guest — English or Bengali..." data-testid="studio-input" disabled={busy}/>
          <Button onClick={() => send()} disabled={busy} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="studio-send"><Send className="w-4 h-4"/></Button>
        </div>
      </div>

      {/* Analysis panel (right) */}
      <div className="bg-white/95 rounded-2xl border border-[#E7C2A6]/40 p-4 overflow-y-auto" style={{ maxHeight: "74vh" }}>
        <h3 className="font-serif text-lg mb-3 flex items-center gap-2"><HelpCircle className="w-4 h-4 text-[#B84F3E]"/>Knowledge Used</h3>
        {analysis?.matched?.length ? (
          <div className="space-y-2">
            {analysis.matched.map((m, i) => (
              <div key={i} className="p-3 rounded-lg bg-[#F5F2EB] border border-[#E7C2A6]/40">
                <div className="flex justify-between text-xs text-[#8A7970]"><span>{m.item.category}</span><span>Priority: {m.item.priority} · Score: {m.score.toFixed(1)}</span></div>
                <div className="text-xs mt-1"><b>Trigger:</b> {m.item.trigger}</div>
                <div className="text-xs mt-1"><b>Response:</b> {(m.item.response || "").slice(0, 180)}{(m.item.response || "").length > 180 ? "…" : ""}</div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[#8A7970]">Send a message to see which lessons the AI used. If nothing matches, hit <b>Teach</b> on the AI reply to lock in the perfect answer.</p>}
      </div>
    </div>
  );
}

// -------- LIBRARY TAB --------
function LibraryTab() {
  const [items, setItems] = useState([]);
  const [suite, setSuite] = useState(null);
  const [running, setRunning] = useState(false);
  const [form, setForm] = useState({ category: "Basic", question: "", expected_keywords: [], active: true });
  const load = () => { api.get("/studio/questions").then(r => setItems(r.data)).catch(()=>{}); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.question) return toast.error("Question required");
    try {
      await api.post("/studio/questions", { ...form, expected_keywords: (form.expected_keywords || []).filter(Boolean) });
      toast.success("Saved");
      setForm({ category: "Basic", question: "", expected_keywords: [], active: true });
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => { await api.delete(`/studio/questions/${id}`); load(); };

  const runSuite = async () => {
    setRunning(true); setSuite(null);
    try {
      const r = await api.post("/studio/run-suite");
      setSuite(r.data);
      toast.success(`Suite done: ${r.data.summary.passed}/${r.data.summary.total} passed`);
    } catch { toast.error("Failed"); }
    finally { setRunning(false); }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-xl">Add Test Question</h3>
          <Button onClick={runSuite} disabled={running || items.length === 0} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="run-suite-btn">
            <Play className="w-4 h-4 mr-2"/>{running ? "Running..." : "Run Test Suite"}
          </Button>
        </div>
        <div className="grid sm:grid-cols-4 gap-2">
          <select className="h-10 rounded-md border border-[#E7C2A6] bg-white px-3" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
            {["Basic","Package","Discount","Booking","Children","Difficult","Location","Food","Custom"].map(c => <option key={c}>{c}</option>)}
          </select>
          <Input className="sm:col-span-2" placeholder='e.g. "Do you have a swimming pool?"' value={form.question} onChange={e => setForm({...form, question: e.target.value})} data-testid="q-input"/>
          <Button onClick={save} className="bg-[#B84F3E] hover:bg-[#9A3F30]"><Plus className="w-4 h-4 mr-1"/>Add</Button>
        </div>
        <Input className="mt-2" placeholder="Expected keywords (comma-separated, optional). Example: pool, no" value={(form.expected_keywords||[]).join(",")} onChange={e => setForm({...form, expected_keywords: e.target.value.split(",").map(s => s.trim())})}/>
      </div>

      {suite && (
        <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40" data-testid="suite-summary">
          <h3 className="font-serif text-xl mb-3">Suite Results</h3>
          <div className="grid grid-cols-4 gap-3 mb-4">
            <Stat label="Total" value={suite.summary.total}/>
            <Stat label="Passed" value={suite.summary.passed} color="#477A5D"/>
            <Stat label="Partial" value={suite.summary.partial} color="#D99B41"/>
            <Stat label="Failed" value={suite.summary.failed} color="#B84F3E"/>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {suite.results.map((r, i) => (
              <div key={i} className="p-3 rounded bg-[#F5F2EB] text-xs">
                <div className="flex justify-between mb-1"><b>{r.question}</b><span className={`px-2 py-0.5 rounded text-white ${r.status === 'pass' ? 'bg-[#477A5D]' : r.status === 'partial' ? 'bg-[#D99B41]' : r.status === 'fail' ? 'bg-[#B84F3E]' : 'bg-[#8A7970]'}`}>{r.status}</span></div>
                <div className="text-[#8A7970]">{(r.reply||"").slice(0, 300)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white/95 rounded-xl border border-[#E7C2A6]/40 divide-y divide-[#E7C2A6]/30">
        {items.map(q => (
          <div key={q.id} className="p-3 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs text-[#8A7970]">{q.category}</div>
              <div className="text-sm">{q.question}</div>
              {q.expected_keywords?.length ? <div className="text-xs text-[#8A7970]">expect: {q.expected_keywords.join(", ")}</div> : null}
            </div>
            <Button size="sm" variant="outline" onClick={() => del(q.id)}><Trash2 className="w-3 h-3"/></Button>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-center text-[#8A7970] text-sm">No test questions saved yet.</div>}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="p-3 rounded-lg bg-[#F5F2EB] text-center">
      <div className="text-xs text-[#8A7970]">{label}</div>
      <div className="font-serif text-2xl" style={{ color: color || "#4A403B" }}>{value}</div>
    </div>
  );
}

// -------- IMAGES TAB --------
function ImagesTab() {
  const [imgs, setImgs] = useState([]);
  const [form, setForm] = useState({ title: "", category: "Deluxe Room", description: "", url: "", file: null, preview: "", active: true, media_type: "image" });
  const [testQ, setTestQ] = useState("");
  const [matched, setMatched] = useState([]);
  const [mediaFilter, setMediaFilter] = useState("all");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/studio/images").then(r => setImgs(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    const isVideo = file.type.startsWith("video/");
    const maxMB = isVideo ? 60 : 5;
    if (file.size > maxMB * 1024 * 1024) return toast.error(`Max ${maxMB}MB for ${isVideo ? "videos" : "photos"}`);
    setForm(f => ({ ...f, file, preview: URL.createObjectURL(file), url: "", media_type: isVideo ? "video" : "image" }));
  };

  const save = async () => {
    if (!form.title || (!form.file && !form.url)) return toast.error("Title + photo/video required");
    setSaving(true);
    try {
      if (form.file) {
        const fd = new FormData();
        fd.append("file", form.file);
        fd.append("title", form.title);
        fd.append("category", form.category);
        fd.append("description", form.description);
        fd.append("share_with_guests", "false");
        fd.append("active", "true");
        await api.post("/studio/media-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/studio/images", form);
      }
      toast.success("Saved");
      setForm({ title: "", category: "Deluxe Room", description: "", url: "", file: null, preview: "", active: true, media_type: "image" });
      load();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const toggle = async (i, v) => { await api.post("/studio/images", {...i, active: v}); load(); };
  const shareToggle = async (i, v) => { await api.post("/studio/images", {...i, share_with_guests: v}); toast.success(v ? "Will be shared with guests" : "No longer shared"); load(); };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/studio/images/${id}`); load(); } };

  const testMatch = async () => {
    if (!testQ) return;
    const r = await api.get("/studio/images/for-query", { params: { q: testQ } });
    setMatched(r.data.images);
  };

  const visible = imgs.filter(i => mediaFilter === "all" ? true : mediaFilter === "video" ? i.media_type === "video" : i.media_type !== "video");

  return (
    <div className="space-y-4">
      <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40">
        <h3 className="font-serif text-xl mb-1">Upload New Photo or Video</h3>
        <p className="text-xs text-[#8A7970] mb-3">Only items marked <b>Guest-shareable</b> below are sent on WhatsApp when guests ask for photos/videos — photos and videos go <b>together at a glance</b> (up to 10 photos + 6 videos). Videos up to 60MB (MP4 works best on WhatsApp).</p>
        <div className="flex gap-2 mb-3">
          <button onClick={() => setForm({...form, media_type: "image", file: null, preview: "", url: ""})} className={`px-4 py-1.5 rounded-full text-xs border ${form.media_type === "image" ? "bg-[#B84F3E] text-white border-[#B84F3E]" : "bg-white border-[#E7C2A6]"}`} data-testid="media-type-photo">📷 Photo</button>
          <button onClick={() => setForm({...form, media_type: "video", file: null, preview: "", url: ""})} className={`px-4 py-1.5 rounded-full text-xs border ${form.media_type === "video" ? "bg-[#B84F3E] text-white border-[#B84F3E]" : "bg-white border-[#E7C2A6]"}`} data-testid="media-type-video">🎬 Video</button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input placeholder="Title (e.g., Deluxe Room Walkthrough)" value={form.title} onChange={e => setForm({...form, title: e.target.value})}/>
          <select className="h-10 rounded-md border border-[#E7C2A6] bg-white px-3" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
            {["Property","Exterior","Deluxe Room","Super Deluxe Room","Bathroom","Food","Bengali Meals","Breakfast","Family/Common Area","Location","Nearby Attractions","Google Review","Other"].map(c => <option key={c}>{c}</option>)}
          </select>
          <Input className="sm:col-span-2" placeholder="Description (keywords help the AI match it to guest questions)" value={form.description} onChange={e => setForm({...form, description: e.target.value})}/>
          <input type="file" accept={form.media_type === "video" ? "video/*" : "image/*"} onChange={e => upload(e.target.files[0])} data-testid="img-upload"/>
          {form.preview && (form.media_type === "video"
            ? <video src={form.preview} controls className="h-24 rounded bg-black"/>
            : <img src={form.preview} alt="preview" className="h-24 object-cover rounded"/>)}
        </div>
        <Button onClick={save} disabled={saving} className="mt-3 bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="save-media-btn"><Save className="w-4 h-4 mr-2"/>{saving ? "Uploading..." : (form.media_type === "video" ? "Save Video" : "Save Photo")}</Button>
      </div>

      <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40">
        <h3 className="font-serif text-xl mb-3">Test: "Which images will AI send?"</h3>
        <div className="flex gap-2">
          <Input placeholder='e.g. "Show me the Deluxe Room"' value={testQ} onChange={e => setTestQ(e.target.value)}/>
          <Button onClick={testMatch} className="bg-[#B84F3E] hover:bg-[#9A3F30]">Test</Button>
        </div>
        {matched.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2">
            {matched.map(i => <div key={i.id} className="text-center"><img src={i.url} className="h-20 w-full object-cover rounded"/><div className="text-xs mt-1">{i.title}</div></div>)}
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        {imgs.length > 0 && (
          <div className="col-span-full flex gap-2">
            {[["all", "All"], ["image", "Photos"], ["video", "Videos"]].map(([k, l]) => (
              <button key={k} onClick={() => setMediaFilter(k)} data-testid={`filter-${k}`}
                className={`px-3 py-1 rounded-full text-xs border ${mediaFilter === k ? "bg-[#B84F3E] text-white border-[#B84F3E]" : "bg-white border-[#E7C2A6]"}`}>{l} ({k === "all" ? imgs.length : imgs.filter(i => k === "video" ? i.media_type === "video" : i.media_type !== "video").length})</button>
            ))}
          </div>
        )}
        {visible.map(i => (
          <div key={i.id} className="bg-white/95 rounded-lg border border-[#E7C2A6]/40 overflow-hidden">
            {i.media_type === "video"
              ? <video src={i.url} controls preload="metadata" className="h-32 w-full object-cover bg-black"/>
              : <img src={i.url} alt={i.title} className="h-32 w-full object-cover"/>}
            <div className="p-3">
              <div className="text-xs text-[#8A7970]">{i.category} {i.media_type === "video" && <span className="ml-1 px-1.5 py-0.5 rounded bg-[#B84F3E] text-white text-[10px]">VIDEO</span>}</div>
              <div className="text-sm font-medium">{i.title}</div>
              {i.missing && <div className="text-xs text-red-600 mt-1" data-testid={`image-missing-${i.id}`}>File was lost - please delete and re-upload it</div>}
              <div className="flex justify-between items-center mt-2">
                <div className="flex gap-3">
                  <label className="flex items-center gap-1 text-xs text-[#8A7970]"><Switch checked={i.active} onCheckedChange={v => toggle(i, v)}/>Active</label>
                  <label className="flex items-center gap-1 text-xs text-[#8A7970]"><Switch checked={!!i.share_with_guests} onCheckedChange={v => shareToggle(i, v)} data-testid={`share-toggle-${i.id}`}/>Guest-shareable</label>
                </div>
                <Button size="sm" variant="outline" onClick={() => del(i.id)}><Trash2 className="w-3 h-3"/></Button>
              </div>
            </div>
          </div>
        ))}
        {imgs.length === 0 && <div className="col-span-full p-6 text-center text-[#8A7970]">No photos or videos yet. Upload above.</div>}
      </div>
    </div>
  );
}

// -------- LINKS TAB --------
function LinksTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const check = async () => {
    setBusy(true);
    try { const r = await api.get("/studio/check-links"); setData(r.data); }
    catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <Button onClick={check} disabled={busy} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="check-links-btn">{busy ? "Checking..." : "Check All Knowledge Links"}</Button>
      {data && (
        <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40">
          <div className="mb-3 text-sm">Scanned <b>{data.total_items}</b> items · <b className="text-[#B84F3E]">{data.items_with_issues}</b> with issues</div>
          <div className="space-y-3">
            {data.issues.map(it => (
              <div key={it.id} className="p-3 bg-[#F5F2EB] rounded">
                <div className="text-xs text-[#8A7970]">{it.category}</div>
                <div className="text-sm mb-2">{it.trigger}</div>
                {it.issues.map((iss, i) => (
                  <div key={i} className="text-xs text-[#B84F3E]">⚠ {iss.type}: {iss.found} {iss.suggestion && `→ ${iss.suggestion}`}</div>
                ))}
              </div>
            ))}
            {data.issues.length === 0 && <div className="text-[#477A5D] text-sm">✅ All links are clean!</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// -------- FAQ DOCUMENT TAB (Word file training + AI source switch + Google reputation) --------
function FAQDocTab() {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rep, setRep] = useState({ rating: "", reviews_count: "", maps_link: "", snippets: [] });

  const load = async () => {
    try {
      const [d, r] = await Promise.all([
        api.get("/studio/faq-doc"),
        api.get("/studio/reputation"),
      ]);
      setDoc(d.data && d.data.count ? d.data : null);
      setRep({ rating: r.data.rating || "", reviews_count: r.data.reviews_count || "", maps_link: r.data.maps_link || "", snippets: r.data.snippets || [] });
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".docx")) return toast.error("Please upload a .docx Word file");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/studio/faq-doc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Parsed ${r.data.count} Q&A pairs from ${r.data.filename} — this is now the AI's only brain`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  const removeDoc = async () => {
    if (!window.confirm("Remove the Word FAQ document? The AI will temporarily fall back to old knowledge until you upload a new file.")) return;
    await api.delete("/studio/faq-doc");
    toast.success("Word FAQ removed");
    load();
  };

  const saveRep = async () => {
    try {
      await api.post("/studio/reputation", { ...rep, snippets: (rep.snippets || []).filter(Boolean) });
      toast.success("Google rating & reviews saved — AI will share these");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-4">
      {/* Master doc status */}
      <div className="bg-[#FBE8DA] rounded-xl p-4 border border-[#E7C2A6]/40 text-sm" data-testid="faq-master-note">
        <b>One master Word file = the AI's entire brain.</b> Once uploaded, the AI answers <b>only</b> from this document (plus the greeting and payment/pricing logic). Answers can include image/video share links. Uploading a new file replaces the old one — no other settings needed.
      </div>

      {/* Upload */}
      <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40" data-testid="faq-upload-card">
        <h3 className="font-serif text-xl mb-1">Upload Master FAQ Word File (.docx)</h3>
        <p className="text-xs text-[#8A7970] mb-3">Format: each question ends with <b>?</b> (or starts with "Q:"), answer on the following lines. Answers may include image/video share links — the AI will pass them to guests.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <input type="file" accept=".docx" onChange={e => upload(e.target.files[0])} disabled={busy} data-testid="faq-file-input"/>
          {busy && <span className="text-sm text-[#8A7970]">Parsing...</span>}
          {doc && <Button size="sm" variant="outline" className="text-[#B84F3E] border-[#B84F3E]" onClick={removeDoc} data-testid="faq-remove-btn"><Trash2 className="w-3 h-3 mr-1"/>Remove Document</Button>}
        </div>
        {doc && (
          <div className="mt-4">
            <div className="text-sm mb-2"><b>{doc.filename}</b> · {doc.count} Q&A pairs · uploaded {new Date(doc.uploaded_at).toLocaleString()}</div>
            <div className="max-h-80 overflow-y-auto divide-y divide-[#E7C2A6]/30 border border-[#E7C2A6]/40 rounded-lg" data-testid="faq-parsed-list">
              {doc.items.map((it, i) => (
                <div key={i} className="p-3">
                  <div className="text-sm font-medium">Q{i + 1}. {it.question}</div>
                  <div className="text-xs text-[#8A7970] mt-1 whitespace-pre-wrap">{it.answer}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Google reputation */}
      <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40" data-testid="reputation-card">
        <h3 className="font-serif text-xl mb-1">Google Rating & Reviews</h3>
        <p className="text-xs text-[#8A7970] mb-3">The AI shares these when guests ask about ratings or reviews. To send review <b>screenshots</b>, upload them in Image Library with "Google Review" in the title and mark them Guest-shareable.</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-xs text-[#8A7970]">Google rating (e.g. 4.8)</label><Input value={rep.rating} onChange={e => setRep({ ...rep, rating: e.target.value })} data-testid="rep-rating"/></div>
          <div><label className="text-xs text-[#8A7970]">Number of reviews</label><Input value={rep.reviews_count} onChange={e => setRep({ ...rep, reviews_count: e.target.value })} data-testid="rep-count"/></div>
          <div><label className="text-xs text-[#8A7970]">Google Maps link</label><Input value={rep.maps_link} onChange={e => setRep({ ...rep, maps_link: e.target.value })} data-testid="rep-link"/></div>
        </div>
        <label className="text-xs text-[#8A7970] mt-3 block">Review snippets (one per line — copy a few best lines from Google)</label>
        <Textarea rows={4} value={(rep.snippets || []).join("\n")} onChange={e => setRep({ ...rep, snippets: e.target.value.split("\n") })} placeholder="Rooms were spotless and the food felt like home. — Google review" data-testid="rep-snippets"/>
        <Button onClick={saveRep} className="mt-3 bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="rep-save"><Save className="w-4 h-4 mr-1"/>Save Rating & Reviews</Button>
      </div>
    </div>
  );
}

// -------- UNANSWERED QUESTIONS TAB --------
function UnansweredTab() {
  const [items, setItems] = useState([]);
  const [showAdded, setShowAdded] = useState(false);
  const load = () => api.get("/studio/unanswered").then(r => setItems(r.data)).catch(() => toast.error("Could not load questions"));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/studio/unanswered/${id}`, { status });
    toast.success(status === "added" ? "Marked as added to the Word file" : "Moved back to open");
    load();
  };
  const remove = async (id) => { await api.delete(`/studio/unanswered/${id}`); load(); };

  const open = items.filter(i => i.status === "open");
  const shown = showAdded ? items : open;
  return (
    <div className="space-y-4">
      <div className="bg-[#FBE8DA] rounded-xl p-4 border border-[#E7C2A6]/40 text-sm" data-testid="unanswered-note">
        Questions the AI could not answer from your Word file. Real guest questions are also sent to Puspen Da's WhatsApp (8348009000) — keep that chat open by sending the bot any message once a day. The guest was asked to WhatsApp or Call Puspen Da 8348009000 or Parna Di 7001154315. Add the answer to your Word file, upload it again, then tap <b>Mark as added</b>.
      </div>
      <div className="flex items-center justify-between">
        <div className="text-sm" data-testid="unanswered-open-count"><b>{open.length}</b> open question{open.length === 1 ? "" : "s"}</div>
        <div className="flex items-center gap-3">
          <label className="text-sm flex items-center gap-2"><Switch checked={showAdded} onCheckedChange={setShowAdded} data-testid="unanswered-show-added"/>Show added</label>
          <Button size="sm" variant="outline" onClick={load} data-testid="unanswered-refresh"><RefreshCw className="w-3 h-3 mr-1"/>Refresh</Button>
        </div>
      </div>
      <div className="bg-white/95 rounded-xl border border-[#E7C2A6]/40 divide-y divide-[#E7C2A6]/30" data-testid="unanswered-list">
        {shown.length === 0 && <div className="p-6 text-sm text-[#8A7970]" data-testid="unanswered-empty">Nothing here — the AI answered everything.</div>}
        {shown.map(q => (
          <div key={q.id} className="p-4 flex items-start justify-between gap-4" data-testid={`unanswered-item-${q.id}`}>
            <div>
              <div className="text-sm font-medium">{q.question}</div>
              <div className="text-xs text-[#8A7970] mt-1">
                {q.phone ? `Guest ${q.phone}` : "Chat & Test"} · {new Date(q.created_at).toLocaleString()} {q.status === "added" && "· added"}
                {q.phone && <span data-testid={`unanswered-alert-${q.id}`}> · {q.alert_sent ? "WhatsApp alert sent to host" : "host alert not delivered"}</span>}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {q.status === "open"
                ? <Button size="sm" className="bg-[#B84F3E] hover:bg-[#9A3F30]" onClick={() => setStatus(q.id, "added")} data-testid={`unanswered-mark-${q.id}`}><Check className="w-3 h-3 mr-1"/>Mark as added</Button>
                : <Button size="sm" variant="outline" onClick={() => setStatus(q.id, "open")} data-testid={`unanswered-reopen-${q.id}`}>Reopen</Button>}
              <Button size="sm" variant="ghost" onClick={() => remove(q.id)} data-testid={`unanswered-delete-${q.id}`}><Trash2 className="w-3 h-3"/></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// -------- FOLLOW-UPS TAB (3h / 9h / 20h WhatsApp nudges) --------
function FollowupsTab() {
  const [s, setS] = useState({ enabled: false, delays_hours: [3, 9, 20], messages: ["", "", ""] });
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [sr, qr] = await Promise.all([api.get("/followups/settings"), api.get("/followups/queue")]);
      const d = sr.data || {};
      const msgs = [...(d.messages || [])];
      while (msgs.length < 3) msgs.push("");
      setS({ enabled: !!d.enabled, delays_hours: d.delays_hours || [3, 9, 20], messages: msgs.slice(0, 3) });
      setQueue(qr.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/followups/settings", { enabled: s.enabled, delays_hours: s.delays_hours, messages: s.messages });
      toast.success("Follow-up settings saved");
      load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const cancel = async (phone) => { await api.post(`/followups/cancel/${encodeURIComponent(phone)}`); load(); };

  const runNow = async () => {
    try {
      const r = await api.post("/followups/run-now");
      toast.success(`Checked queue — ${r.data.sent || 0} follow-up(s) sent`);
      load();
    } catch { toast.error("Run failed"); }
  };

  return (
    <div className="space-y-4">
      <div className={`rounded-xl p-4 border text-sm flex items-start gap-3 ${s.enabled ? "bg-[#EEF6EF] border-[#477A5D]/40" : "bg-[#FBE8DA] border-[#B84F3E]/40"}`} data-testid="followup-status">
        <Clock className="w-5 h-5 shrink-0 mt-0.5"/>
        <div>
          {s.enabled ? (
            <><b>✅ Auto reminders are LIVE.</b> Any guest who messages your WhatsApp and goes silent will automatically receive the 3-stage nudge sequence (3h → 9h → 20h). Smart-stops if they reply or book.</>
          ) : (
            <><b>⚠️ Auto reminders are PAUSED.</b> Toggle ON below to activate the 3h / 9h / 20h follow-up sequence. Guests already in the queue will not receive any messages until you enable it.</>
          )}
        </div>
      </div>

      <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40" data-testid="followup-settings">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div>
            <h3 className="font-serif text-xl">WhatsApp Follow-up Sequence</h3>
            <p className="text-xs text-[#8A7970]">When a guest goes silent, the bot nudges them after <b>3h</b>, then <b>9h</b>, then <b>20h</b>. Stops automatically if the guest replies or a booking exists.</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{s.enabled ? "ON" : "OFF"}</span>
            <Switch checked={s.enabled} onCheckedChange={v => setS({ ...s, enabled: v })} data-testid="followup-toggle"/>
          </div>
        </div>
        {[0, 1, 2].map(i => (
          <div key={i} className="mt-3">
            <label className="text-xs text-[#8A7970]">Message {i + 1} — sent after {s.delays_hours[i]}h of silence</label>
            <Textarea rows={2} value={s.messages[i]} onChange={e => { const m = [...s.messages]; m[i] = e.target.value; setS({ ...s, messages: m }); }} data-testid={`followup-msg-${i + 1}`}/>
          </div>
        ))}
        <div className="flex gap-2 mt-4">
          <Button onClick={save} disabled={busy} className="bg-[#B84F3E] hover:bg-[#9A3F30]" data-testid="followup-save"><Save className="w-4 h-4 mr-1"/>Save</Button>
          <Button variant="outline" onClick={runNow} data-testid="followup-run-now"><Play className="w-4 h-4 mr-1"/>Run Now (test)</Button>
        </div>
        <p className="text-xs text-[#8A7970] mt-2">In production the scheduler checks every 15 minutes, so actual send time is within ±15 min of the target.</p>
      </div>

      <div className="bg-white/95 rounded-xl border border-[#E7C2A6]/40" data-testid="followup-queue">
        <div className="p-4 border-b border-[#E7C2A6]/40 flex justify-between items-center">
          <h3 className="font-serif text-lg">Scheduled Follow-ups</h3>
          <Button size="sm" variant="outline" onClick={load}><RotateCw className="w-3 h-3 mr-1"/>Refresh</Button>
        </div>
        <div className="divide-y divide-[#E7C2A6]/30 max-h-96 overflow-y-auto">
          {queue.length === 0 && <div className="p-6 text-center text-[#8A7970] text-sm">No follow-ups scheduled yet. They appear automatically when guests message and go silent.</div>}
          {queue.map(q => (
            <div key={q.id || q.phone} className="p-3 flex items-center justify-between gap-3 text-sm">
              <div>
                <div className="font-medium">{q.phone}</div>
                <div className="text-xs text-[#8A7970]">
                  Stage {q.stage}/3 · next: {q.next_run_at ? new Date(q.next_run_at).toLocaleString() : "—"}
                  {q.cancelled && <span className="text-[#B84F3E]"> · cancelled ({q.cancel_reason || "manual"})</span>}
                  {q.done && <span className="text-[#477A5D]"> · completed</span>}
                </div>
              </div>
              {!q.cancelled && !q.done && <Button size="sm" variant="outline" onClick={() => cancel(q.phone)}>Stop</Button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// -------- BACKGROUND TAB --------
function BgTab({ bg, setBg }) {
  const [local, setLocal] = useState(bg);
  useEffect(() => setLocal(bg), [bg]);
  const upload = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => setLocal({...local, background_url: r.result});
    r.readAsDataURL(file);
  };
  const save = async () => { await api.post("/studio/settings", local); setBg(local); toast.success("Saved"); };
  return (
    <div className="bg-white/95 rounded-xl p-5 border border-[#E7C2A6]/40 max-w-2xl">
      <h3 className="font-serif text-xl mb-3">Studio Background</h3>
      <input type="file" accept="image/*" onChange={e => upload(e.target.files[0])} className="mb-3"/>
      {local.background_url && <img src={local.background_url} alt="bg" className="h-40 object-cover rounded mb-3"/>}
      <label className="text-xs text-[#8A7970]">Opacity: {Math.round(local.background_opacity*100)}%</label>
      <input type="range" min="0" max="0.8" step="0.05" value={local.background_opacity} onChange={e => setLocal({...local, background_opacity: parseFloat(e.target.value)})} className="w-full mb-3"/>
      <div className="flex gap-2">
        <Button onClick={save} className="bg-[#B84F3E] hover:bg-[#9A3F30]">Save</Button>
        <Button variant="outline" onClick={() => setLocal({ background_url: "", background_opacity: 0.2 })}>Remove</Button>
      </div>
    </div>
  );
}
